import cx from 'classnames'
import { insertAndFocus } from 'common/Prose/utils/insertAndFocus'
import {
  AddImageFromLink,
  Giphy,
  Integration,
  Unsplash,
  UploadMedia,
  UploadsLibrary,
} from 'proseShared/IntegrationsIcons'
import { Node as ProseNode } from 'prosemirror-model'
import { type EditorView } from 'prosemirror-view'
import { useEffect, useRef, useState } from 'react'
import { SelectedImage } from 'types'
import Styles from '../Prompt.module.scss'
import PromptFooter from './components/PromptFooter/PromptFooter'
import Sidebar from './Sidebar'
import UploadFromComputer from './UploadFromComputer'
import UploadFromGiphy from './UploadFromGiphy'
import UploadFromLink from './UploadFromLink'
import UploadFromUnsplash from './UploadFromUnsplash'
import { UploadFromLibrary } from './components/UploadFromLibrary/UploadFromLibrary'
import { FocusScope } from '@react-aria/focus'
import { useMediaUploadPrompt } from './hooks/useMediaUploadPrompt'
import { IStackManager } from 'almanac-ui/Modal/Types'
import { REACT_APP_CLOUDINARY_CLOUD_NAME } from 'env'
import { reportError } from 'utils/error'
import { ImagePickerModal } from 'components/ImagePickerModal/ImagePickerModal'
import { ImagePickerProviderProps } from 'components/ImagePickerModal/ImagePickerContextTypes'
import { Provider as IProvider } from 'components/ImagePickerModal/Types'
import { ImageNodeAttrs } from 'common/Prose/nodeViews/Image/Types'

interface Props {
  // this is empty when user clicks "Cancel" within the prompt
  onSubmit: (payload?: SelectedImage[], saveInUploadsFolder?: boolean) => void
  preselectedIntegration?: Integration
  multiSelect?: boolean
  uploadHighRes?: boolean
  classNames?: string
  initialQuery?: string
  inEditor?: boolean
  hiddenProviders?: Set<IProvider>
}

export const MediaUploadPromptContent = ({
  preselectedIntegration,
  onSubmit,
  multiSelect = true,
  uploadHighRes = false,
  classNames,
  initialQuery,
  inEditor = true,
  hiddenProviders,
}: Props) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [defaultUploadContainer, setDefaultUploadContainer] = useState(true)

  const {
    onActiveIntegrationChange,
    onSuccess,
    onCancel,
    uploadImages,
    loading,
    stagedImages,
    setStagedImages,
    selectedImages,
    setSelectedImages,
    activeIntegration,
  } = useMediaUploadPrompt({ onSubmit, initialIntegration: preselectedIntegration || AddImageFromLink })

  useEffect(() => {
    const mouseOutside = ({ target }: MouseEvent) => {
      if (!containerRef.current?.contains(target as Node)) {
        onSubmit(undefined, activeIntegration !== UploadsLibrary)
      }
    }

    window.addEventListener('mousedown', mouseOutside)

    return () => window.removeEventListener('mousedown', mouseOutside)
  }, [activeIntegration, onSubmit])

  return (
    <FocusScope contain restoreFocus autoFocus>
      <div className={cx(inEditor && Styles.mediaUploadContainer, classNames)} ref={containerRef}>
        <Sidebar
          activeIntegration={activeIntegration || AddImageFromLink}
          setActiveIntegration={onActiveIntegrationChange}
          hiddenProviders={hiddenProviders}
        />

        <div
          className={cx(Styles.mediaUploadMain, {
            [Styles.defaultUploadContainer]: activeIntegration === UploadMedia && defaultUploadContainer,
          })}
        >
          {activeIntegration === AddImageFromLink && (
            <UploadFromLink
              selectedImages={selectedImages}
              stagedImages={stagedImages}
              setSelectedImages={setSelectedImages}
              setStagedImages={setStagedImages}
              uploadImages={uploadImages}
              onSuccess={onSuccess}
              multiSelect={multiSelect}
            />
          )}

          {activeIntegration === UploadsLibrary && (
            <UploadFromLibrary
              selectedImages={selectedImages}
              stagedImages={stagedImages}
              setSelectedImages={setSelectedImages}
              setStagedImages={setStagedImages}
              multiSelect={multiSelect}
            />
          )}

          {activeIntegration === UploadMedia && (
            <UploadFromComputer
              selectedImages={selectedImages}
              stagedImages={stagedImages}
              setDefaultUploadContainer={setDefaultUploadContainer}
              setSelectedImages={setSelectedImages}
              setStagedImages={setStagedImages}
              multiSelect={multiSelect}
            />
          )}

          {activeIntegration === Unsplash && (
            <UploadFromUnsplash
              selectedImages={selectedImages}
              stagedImages={stagedImages}
              setSelectedImages={setSelectedImages}
              setStagedImages={setStagedImages}
              multiSelect={multiSelect}
              uploadHighRes={uploadHighRes}
              initialQuery={initialQuery}
            />
          )}

          {activeIntegration === Giphy && (
            <UploadFromGiphy
              selectedImages={selectedImages}
              stagedImages={stagedImages}
              setSelectedImages={setSelectedImages}
              setStagedImages={setStagedImages}
              multiSelect={multiSelect}
            />
          )}

          <PromptFooter
            stagedImagesCount={stagedImages.length}
            forceShow={activeIntegration === UploadsLibrary}
            selectedImagesCount={selectedImages.length}
            onSuccess={onSuccess}
            renderGiphyLogo={activeIntegration === Giphy}
            onCancel={onCancel}
            loading={loading}
            multiSelect={selectedImages.length > 1 && multiSelect}
          />
        </div>
      </div>
    </FocusScope>
  )
}

const openImagePicker = ({
  view,
  stackManager,
  ...props
}: Omit<ImagePickerProviderProps, 'uploadImages' | 'children'> & {
  view: EditorView
  stackManager: IStackManager
}) => {
  let resolveRef: Function

  const closePromise = new Promise<SelectedImage[]>((resolve) => {
    resolveRef = resolve
  })

  const uploadImages = async (files: File[]) => {
    const formData = new FormData()
    const payload: SelectedImage[] = []

    try {
      for (let file of files) {
        formData.append('file', file)
        formData.append('upload_preset', 'workspace_portal_uploads')

        const response = await fetch(
          `https://api.cloudinary.com/v1_1/${REACT_APP_CLOUDINARY_CLOUD_NAME}/image/upload`,
          {
            method: 'POST',
            body: formData,
          },
        )
        const parsedResponse = await response.json()
        payload.push({ ...parsedResponse, url: parsedResponse.secure_url || parsedResponse.url })
      }
    } catch (error) {
      reportError(error, { severity: 'error' })
    }

    return payload
  }

  stackManager.openModal(ImagePickerModal, {
    ...props,
    onConfirm: (images: SelectedImage[]) => {
      stackManager.closeModal()
      view.focus()
      if (images) {
        resolveRef(images)
      }
    },
    onCancel: () => {
      stackManager.closeModal()
      view.focus()
    },
    uploadImages,
  })

  return closePromise
}

const getProviderFromIntegration = (integration: Integration): IProvider => {
  switch (integration.name) {
    case UploadsLibrary.name:
      return 'uploads'
    case AddImageFromLink.name:
      return 'link'
    case Unsplash.name:
      return 'stock'
    case Giphy.name:
      return 'gifs'
    default:
      return 'uploads'
  }
}

export const openMediaUploadPrompt = async ({
  view,
  preselectedIntegration,
  multiSelect = true,
  initialQuery,
  stackManager,
}: {
  view: EditorView
  preselectedIntegration?: Integration
  multiSelect?: boolean
  initialQuery?: string
  stackManager: IStackManager
}) => {
  return openImagePicker({
    view,
    stackManager,
    initialQuery,
    maxCount: multiSelect ? Infinity : 1,
    defaultProvider: getProviderFromIntegration(preselectedIntegration || UploadMedia),
  })
}

export const openImagePromptAndInsert = async ({
  view,
  stackManager,
}: {
  view: EditorView
  stackManager: IStackManager
}) => {
  const images = await openMediaUploadPrompt({ view, stackManager })

  if (images) {
    images.forEach((image) => {
      let attrs: Partial<ImageNodeAttrs> = {
        src: image.url,
      }

      if (image.unsplash) {
        attrs.source = 'unsplash'
        attrs.unsplashQuery = image.unsplash.query
        attrs.unsplashId = image.unsplash.id
      }

      const node = view.state.schema.nodes.image.create(attrs) as ProseNode
      insertAndFocus(view, node)
    })
  }
}
